import { IconButton } from "@mui/material";
import { VolumeUp as VolumeUpIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useState } from "react";

/**
 * PreviewAudioButton - A reusable button component for audio playback
 *
 * @param {Object} props
 * @param {string} props.audioUrl - The audio url to be played (required)
 * @param {Function} props.onPlayAudio - Optional callback to play audio (for mobile compatibility)
 * @param {boolean} props.audioPlaying - Optional state indicating if audio is playing
 * @param {boolean} props.isShowAllOptions - Optional state indicating if all options should be shown
 * @param {Object} props.style - Optional style object for custom styling
 *
 * @returns {JSX.Element} - A button that plays audio when clicked
 */
const PreviewAudioButton = ({
  audioUrl,
  onPlayAudio,
  audioPlaying,
  isShowAllOptions,
  style,
}) => {
  const [playingAudio, setPlayingAudio] = useState(null);

  const handleClick = () => {
    // If onPlayAudio callback is provided (mobile-friendly mode), use it
    if (onPlayAudio) {
      onPlayAudio(audioUrl);
      return;
    }

    // Otherwise use internal audio element (desktop fallback)
    if (playingAudio === audioUrl) {
      setPlayingAudio(null);
    } else {
      setPlayingAudio(audioUrl);
    }
  };

  return audioUrl ? (
    isShowAllOptions ? (
      <audio
        src={audioUrl}
        autoPlay
        onEnded={() => setPlayingAudio(null)}
        controls
        style={{ display: "block", width: "100%", ...style }}
      />
    ) : (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          onClick={handleClick}
          size="small"
          color={
            audioPlaying || playingAudio === audioUrl ? "primary" : "default"
          }
        >
          <VolumeUpIcon />
        </IconButton>
        {/* Only render internal audio element if not using external player */}
        {!onPlayAudio && playingAudio === audioUrl && (
          <audio
            src={audioUrl}
            autoPlay
            onEnded={() => setPlayingAudio(null)}
            style={{ display: "none" }}
          />
        )}
      </Box>
    )
  ) : (
    <></>
  );
};

export default PreviewAudioButton;
